import { useCallback, useEffect, useState } from 'react';

import { billingApi } from '../../api/billing.js';
import { pipelineApi } from '../../api/pipeline.js';
import { ErrorState, LoadingState } from '../../components/ui';
import { RecordDetailBody } from '../Records/RecordDetailRow.jsx';
import { renderRateMix, statusLabel } from '../Records/helpers.jsx';
import { useUrlState } from '../../url';
import { RecordActions } from './RecordActions.jsx';

// One billing record on its own page.
//
// This is the link you paste into a ticket, an email to Kristy, or a NetSuite
// comment: it opens the exact record with no filter to reproduce and nothing
// else on screen. The panel itself is the same component the Records table
// expands inline, so the two can never drift apart.
//
// Addressed EITHER by `recordKey` (/records/k/<token> — the durable form) or by
// `recordId` (/records/<id> — in-session only). An id is worthless in a shared
// link: the reprocess rebuilds billing.records, so ids move by tens of
// thousands overnight. An id arrival is therefore rewritten to the /k/ form as
// soon as the record loads, so whatever the reader copies out of the address
// bar is already the durable address.
export default function RecordDetail({ recordId, recordKey }) {
  const { navigate } = useUrlState();
  const [record, setRecord] = useState(null);
  const [error,  setError]  = useState(null);
  const [copied, setCopied] = useState(false);
  // Whether something is already using the NetSuite API — same 15s poll the
  // table runs, so Sync / Void / Re-sync stand down here for the same reason.
  const [nsConflict, setNsConflict] = useState(null);

  // Also the post-action refresh: every button re-reads the record through
  // this, so the status line and the action set always describe the record as
  // it is NOW, not as it was when the link was opened.
  const load = useCallback(async () => {
    try {
      const r = recordKey
        ? await billingApi.getByKey(recordKey)
        : await billingApi.get(recordId);
      setRecord(r);
      setError(null);
    } catch (e) {
      if (e.status === 404 && recordKey) {
        setError('That record no longer exists under this key. The punch may '
          + 'have been deleted in Dayforce, or corrected in a way that re-keyed '
          + 'it (a changed date, site or labor task). Search the employee and '
          + 'date on the Billing Records page.');
      } else if (e.status === 404) {
        setError(`No billing record with id ${recordId}. Record ids are `
          + 'rebuilt by every reprocess, so an id link stops working overnight '
          + '— that is what the "Copy link" button avoids. Search the punch on '
          + 'the Billing Records page.');
      } else {
        setError(e.message);
      }
    }
  }, [recordId, recordKey]);

  useEffect(() => {
    setRecord(null);
    setError(null);
    load();
  }, [recordId, recordKey, load]);

  // Rewrite an id URL to the durable key once we know it. `replace`, not push:
  // the id URL is not a place the Back button should be able to return to.
  useEffect(() => {
    if (recordKey || !record?.record_key) return;
    navigate(['records', 'k', record.record_key], {}, { replace: true });
  }, [recordKey, record, navigate]);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await pipelineApi.getActive();
        if (cancelled) return;
        const c = res?.conflict;
        setNsConflict(c && c.touches_netsuite ? c : null);
      } catch { /* best-effort — a blip shouldn't disable the buttons */ }
    };
    tick();
    const t = setInterval(tick, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard is permission-gated in some browsers; the address bar
      // already holds the link, so this is a convenience, not the mechanism.
    }
  };

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        flexWrap: 'wrap', marginBottom: 4,
      }}>
        <a
          href="/records"
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
            e.preventDefault();
            navigate(['records']);
          }}
          style={{ fontSize: 13, color: '#29417a', textDecoration: 'none' }}
        >
          ← All billing records
        </a>
        <button type="button" className="btn btn-sm btn-secondary" onClick={copyLink}>
          {copied ? 'Link copied' : 'Copy link'}
        </button>
      </div>

      {/* Lead with the PUNCH — it is the durable name of this record and the
          one a reader can match against a Dayforce timesheet or a ticket. The
          numeric id is deliberately demoted and labelled: it is rebuilt by
          every reprocess, so quoting it in a message is a dead reference by
          morning. */}
      <div className="section-title">
        {record?.punch_xref_code
          ? `Billing Record · Punch ${record.punch_xref_code}`
          : 'Billing Record'}
      </div>
      {record?.id != null && (
        <div style={{ fontSize: 11, color: '#999', marginTop: -6, marginBottom: 10 }}>
          internal id {record.id} — changes on every reprocess, don't quote it
        </div>
      )}

      {error && <ErrorState>{error}</ErrorState>}
      {!record && !error && <LoadingState>Loading record…</LoadingState>}

      {record && (
        <>
          <div style={{
            display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center',
            padding: '12px 16px', marginBottom: 16, borderRadius: 6,
            background: '#fff', boxShadow: '0 1px 4px rgba(0,33,89,0.08)',
            fontSize: 13,
          }}>
            <span style={{ fontWeight: 600 }}>
              {record.employee_name || record.employee_xref_code}
            </span>
            <span>{record.business_date}</span>
            <span>{record.gross_hours}h</span>
            <span>{record.ns_location_name || record.site_code || '-'}</span>
            <span>{record.billing_title_name || '-'}</span>
            <span>{renderRateMix(record.rate_levels, record.status)}</span>
            <span className={`badge ${record.status}`}>{statusLabel(record.status)}</span>
            {record.source_type === 'pay_adjustment' && (
              <span style={{ fontSize: 11, color: '#999' }}>Pay Adjustment</span>
            )}
          </div>

          {/* The one way a durable key can mislead. Reprocess apply pass 1
              INSERTs a synthetic 'source_deleted' row for every orphaned
              push_event key, copying employee name, date, location and hours
              off the push-time snapshot — so a link to a record that was
              RE-KEYED (an earlier punch arrived; a labor task was corrected)
              lands on something where every visible field matches what the
              reader expects. Believing it means voiding a live TimeBill for a
              record that is actually alive under a new key, which under-bills
              the customer. The backend answers the question the ghost can't:
              is there a live record for the same employee, day and site? */}
          {record.is_orphan_snapshot && (
            <div style={{
              padding: '12px 16px', marginBottom: 16, borderRadius: 6,
              background: '#fff8e6', color: '#6e4c00',
              border: '1px solid #e6ba59', fontSize: 13,
            }}>
              <strong>⚠ This is a push-time snapshot, not a live billing record.</strong>
              <div style={{ marginTop: 4 }}>
                No current billing record carries this key. The row below was
                reconstructed from what was pushed to NetSuite, so its details
                are historical.
              </div>
              {record.live_siblings?.length > 0 ? (
                <div style={{ marginTop: 8 }}>
                  This punch looks <strong>re-keyed, not deleted</strong> — the
                  same employee, day and site has{' '}
                  {record.live_siblings.length} live record
                  {record.live_siblings.length === 1 ? '' : 's'}. Do not void the
                  NetSuite TimeBill; open the live record instead:
                  <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
                    {record.live_siblings.map((sib) => (
                      <li key={sib.id} style={{ marginTop: 2 }}>
                        <a
                          href={`/records/k/${sib.record_key}`}
                          onClick={(e) => {
                            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                            e.preventDefault();
                            navigate(['records', 'k', sib.record_key]);
                          }}
                        >
                          {sib.punch_xref_code}
                        </a>{' '}
                        <span style={{ color: '#8a6d3b' }}>
                          — {sib.gross_hours}h · {sib.ns_location_name || sib.site_code} · {sib.status}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div style={{ marginTop: 8 }}>
                  No live record exists for this employee, day and site, so the
                  punch really does appear to have been deleted upstream.
                </div>
              )}
            </div>
          )}

          <RecordActions record={record} onChanged={load} nsConflict={nsConflict} />

          <div style={{ background: '#f8f9fc', padding: 16, borderRadius: 6 }}>
            <RecordDetailBody detail={record} />
          </div>
        </>
      )}
    </div>
  );
}
